import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Drawer, Empty, Grid, Segmented, Skeleton } from 'antd';
import { decimal, money, tone } from './format';
import type {
  BasisMetadataLike,
  CostBasisAudience,
  LotDetailLike,
  LotFiltersLike,
  LotsSource,
  TaxLotLike,
} from './types';

export type LotsPosition = {
  cost: string;
  average: string;
  gain: string;
  gainTone: string;
};
type Props = {
  /** Reads lots for one account; its identity keys the list. */
  source: LotsSource;
  accountId: string;
  assetId?: string;
  title: string;
  /** Secondary line under the title, e.g. the security name or account type. */
  description?: string;
  /** Source-reported lot counts; shown on the Held / Sold switch when known. */
  counts?: { open: number; closed: number };
  /** Position totals shown above the lot list for a single security. */
  position?: LotsPosition;
  generation: string;
  audience?: CostBasisAudience;
  onClose: () => void;
};
type Page = {
  records: TaxLotLike[];
  cursor?: string | null;
  total: number;
  metadata?: BasisMetadataLike;
  error?: number;
  loading: boolean;
  reset?: boolean;
};
const COPY = {
  investor: {
    noSales: (symbol?: string) => `You haven't sold any ${symbol || 'shares'} in this account.`,
    taxNote:
      'Realized gains are reported by Apex and may not match your tax forms. Use your 1099-B for taxes.',
    footnote: 'Reported by Apex. For taxes, use your 1099-B.',
  },
  advisor: {
    noSales: (symbol?: string) =>
      `The investor hasn't sold any ${symbol || 'shares'} in this account.`,
    taxNote:
      "Realized gains are reported by Apex and may not match the investor's tax forms. The 1099-B is the tax record.",
    footnote: "Reported by Apex. The investor's 1099-B is the tax record.",
  },
};

export function LotsDrawer({
  source,
  accountId,
  assetId,
  title,
  description,
  counts,
  position,
  generation,
  audience = 'investor',
  onClose,
}: Props) {
  const screens = Grid.useBreakpoint();
  const [state, setState] = useState<'OPEN' | 'CLOSED'>('OPEN');
  const [dates, setDates] = useState<{ date_from?: string; date_to?: string }>({});
  const [refresh, setRefresh] = useState(0);
  const filters = useMemo<LotFiltersLike>(
    () => ({ state, ...(assetId ? { asset_id: assetId } : {}), ...dates }),
    [state, assetId, dates],
  );
  const key = JSON.stringify([accountId, filters, generation, refresh]);
  const label = (text: string, count?: number) => (count != null ? `${text} · ${count}` : text);
  return (
    <Drawer
      open
      title={
        <div className="cost-basis-drawer-title">
          <small>Purchase lots</small>
          <span>
            {title}
            {description && <small> {description}</small>}
          </span>
        </div>
      }
      placement={screens.md ? 'right' : 'bottom'}
      width={480}
      height="85vh"
      onClose={onClose}
      destroyOnClose
      // className lands on .ant-drawer-content itself; the root class lets the
      // stylesheet reach the panel, header and body (incl. the mobile sheet radius).
      rootClassName="cost-basis-drawer"
    >
      {!screens.md && <span className="cost-basis-sheet-handle" aria-hidden="true" />}
      {position && (
        <dl className="cost-basis-position">
          <div>
            <dt>Cost basis</dt>
            <dd>{position.cost}</dd>
          </div>
          <div>
            <dt>Avg cost</dt>
            <dd>{position.average}</dd>
          </div>
          <div>
            <dt>Gain / Loss</dt>
            <dd className={position.gainTone}>{position.gain}</dd>
          </div>
        </dl>
      )}
      <div className="cost-basis-lots-toolbar">
        <Segmented
          aria-label="Lot status"
          value={state}
          options={[
            { label: label('Held', counts?.open), value: 'OPEN' },
            { label: label('Sold', counts?.closed), value: 'CLOSED' },
          ]}
          onChange={(value) => setState(value as 'OPEN' | 'CLOSED')}
        />
        <DatePicker.RangePicker
          aria-label={state === 'OPEN' ? 'Purchase date range' : 'Sale date range'}
          placeholder={state === 'OPEN' ? ['Bought from', 'to'] : ['Sold from', 'to']}
          allowClear
          onChange={(_value, strings) =>
            setDates(strings[0] && strings[1] ? { date_from: strings[0], date_to: strings[1] } : {})
          }
        />
      </div>
      <LotPages
        key={key}
        source={source}
        filters={filters}
        symbol={assetId ? title : undefined}
        expectedGeneration={refresh === 0 ? generation : undefined}
        audience={audience}
        onRefresh={() => setRefresh((v) => v + 1)}
      />
      <p className="cost-basis-muted cost-basis-drawer-footnote">{COPY[audience].footnote}</p>
    </Drawer>
  );
}

export function LotPages({
  source,
  filters,
  symbol,
  expectedGeneration,
  audience = 'investor',
  onRefresh,
}: {
  source: LotsSource;
  filters: LotFiltersLike;
  /** Ticker for a single-security list; used in the empty Sold message. */
  symbol?: string;
  expectedGeneration?: string;
  audience?: CostBasisAudience;
  onRefresh: () => void;
}) {
  const [page, setPage] = useState<Page>({ records: [], total: 0, loading: true });
  const [request, setRequest] = useState<{ cursor?: string; attempt: number }>({ attempt: 0 });
  const [selected, setSelected] = useState<string>();
  useEffect(() => {
    let active = true;
    setPage((old) => ({ ...old, loading: true, error: undefined }));
    void source
      .fetchPage(filters, request.cursor)
      .then((next) => {
        if (!active) return;
        setPage((old) => {
          const pinned = old.metadata?.generation || expectedGeneration;
          if (
            (pinned && pinned !== next.metadata.generation) ||
            !next.metadata.calculation_contract_verified
          )
            return { ...old, loading: false, reset: true };
          if (
            next.records.some(
              (lot) =>
                lot.state !== filters.state ||
                (filters.asset_id && lot.asset_id !== filters.asset_id),
            )
          )
            return { ...old, loading: false, error: 502 };
          const records = request.cursor ? [...old.records, ...next.records] : next.records;
          const duplicate = new Set(records.map((lot) => lot.lot_id)).size !== records.length;
          if (
            duplicate ||
            records.length > next.total_count ||
            (next.next_cursor && next.next_cursor === request.cursor)
          )
            return { ...old, loading: false, reset: true };
          return {
            records,
            total: next.total_count,
            metadata: next.metadata,
            cursor: next.next_cursor,
            loading: false,
          };
        });
      })
      .catch((error: unknown) => {
        const status = source.errorStatus(error);
        if (active && status !== null)
          setPage((old) => ({
            ...old,
            loading: false,
            error: status,
            reset: status === 400 && Boolean(request.cursor),
          }));
      });
    return () => {
      active = false;
    };
  }, [source, filters, expectedGeneration, request]);

  if (page.reset)
    return (
      <Alert
        type="info"
        message="Cost basis was updated"
        description="Refresh to view a consistent set of purchase lots."
        action={<Button onClick={onRefresh}>Refresh</Button>}
      />
    );
  const sold = filters.state === 'CLOSED';
  const filtered = Boolean(filters.date_from || filters.date_to);
  return (
    <div aria-live="polite" className="cost-basis-lots">
      {page.error !== undefined && (
        <Alert
          type="warning"
          message="We couldn't load purchase lots"
          action={
            <Button onClick={() => setRequest((old) => ({ ...old, attempt: old.attempt + 1 }))}>
              Try again
            </Button>
          }
        />
      )}
      {page.records.map((lot) => {
        const open = selected === lot.lot_id;
        const detailId = `cost-basis-lot-${lot.lot_id}`;
        const date =
          lot.state === 'OPEN'
            ? lot.acquisition_date || 'Date unavailable'
            : lot.disposal_date || 'Date unavailable';
        return (
          <div className={`cost-basis-lot${open ? ' is-open' : ''}`} key={lot.lot_id}>
            <button
              type="button"
              className="cost-basis-lot-row"
              aria-expanded={open}
              aria-controls={open ? detailId : undefined}
              onClick={() => setSelected(open ? undefined : lot.lot_id)}
            >
              <span className="cost-basis-lot-main">
                <span className="cost-basis-lot-date">{`${lot.state === 'OPEN' ? 'Bought' : 'Sold'} ${date}`}</span>
                <small>
                  {decimal(lot.quantity, 4)} shares
                  {lot.state === 'CLOSED' && ` · basis ${money(lot.adjusted_basis, lot.currency)}`}
                </small>
              </span>
              {lot.state === 'CLOSED' ? (
                <strong className={tone(lot.realized_gain)}>
                  {money(lot.realized_gain, lot.currency, true)}
                  <small>Realized</small>
                </strong>
              ) : (
                <strong>{money(lot.adjusted_basis, lot.currency)}</strong>
              )}
            </button>
            {open && (
              <LotDetailPanel
                id={detailId}
                source={source}
                lotId={lot.lot_id}
                generation={page.metadata?.generation}
                onRefresh={onRefresh}
              />
            )}
          </div>
        );
      })}
      {page.loading && <Skeleton active paragraph={{ rows: 3 }} />}
      {!page.loading &&
        page.error === undefined &&
        !page.records.length &&
        (page.metadata?.completeness === 'complete' ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              sold && !filtered
                ? COPY[audience].noSales(symbol)
                : 'No purchase lots match these filters'
            }
          />
        ) : (
          <Alert message="Purchase lots are not available yet" type="info" />
        ))}
      {sold && !page.loading && page.error === undefined && (
        <Alert type="warning" className="cost-basis-tax-note" message={COPY[audience].taxNote} />
      )}
      {page.metadata && (
        <p className="cost-basis-muted">
          Showing {page.records.length} of {page.total} · As of{' '}
          {page.metadata.basis_as_of || 'unknown'}
        </p>
      )}
      {page.cursor && !page.error && (
        <Button
          block
          disabled={page.loading}
          onClick={() =>
            setRequest((old) => ({ cursor: page.cursor || undefined, attempt: old.attempt + 1 }))
          }
        >
          Load more
        </Button>
      )}
    </div>
  );
}

function LotDetailPanel({
  id,
  source,
  lotId,
  generation,
  onRefresh,
}: {
  id: string;
  source: LotsSource;
  lotId: string;
  generation?: string | null;
  onRefresh: () => void;
}) {
  const [detail, setDetail] = useState<LotDetailLike>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    void source
      .fetchLot(lotId)
      .then((value) => {
        if (active) setDetail(value);
      })
      .catch((error: unknown) => {
        if (active && source.errorStatus(error) !== null) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [source, lotId]);
  const changed =
    detail &&
    ((detail.metadata.generation ?? null) !== (generation ?? null) ||
      !detail.metadata.calculation_contract_verified);
  return (
    <section id={id} className="cost-basis-lot-detail" aria-label="Purchase lot details">
      {failed ? (
        <Alert message="Unable to load this purchase lot" type="warning" />
      ) : !detail ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : changed ? (
        <Alert
          message="Cost basis was updated"
          action={<Button onClick={onRefresh}>Refresh</Button>}
        />
      ) : (
        <dl>
          <div>
            <dt>Acquired</dt>
            <dd>{detail.record.acquisition_date || '—'}</dd>
          </div>
          <div>
            <dt>Disposed</dt>
            <dd>{detail.record.disposal_date || '—'}</dd>
          </div>
          <div>
            <dt>Shares</dt>
            <dd>{decimal(detail.record.quantity, 4)}</dd>
          </div>
          <div>
            <dt>Adjusted basis</dt>
            <dd>{money(detail.record.adjusted_basis, detail.record.currency)}</dd>
          </div>
          {detail.record.state === 'CLOSED' && (
            <div>
              <dt>Source-reported realized gain</dt>
              <dd className={tone(detail.record.realized_gain)}>
                {money(detail.record.realized_gain, detail.record.currency, true)}
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
}
